import React, {
  ForwardedRef,
  forwardRef,
  RefObject,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  formatDate,
} from 'utils/date';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  pluralize,
} from '@populateui/utils';
import {
  FilterContext,
} from 'core/Filters/context';
import {
  useUserSettings,
} from 'core/hooks';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Select from 'core/components/Select';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import Spinner from 'pages/Dashboard/components/Spinner';
import Button from 'pages/Dashboard/components/Button';
import SettingsButton, {
  CalendarViewSettings,
  defaultCalendarViewSettings,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/SettingsButton';
import CalendarView from 'pages/Dashboard/pages/Appointments/pages/List/components/CalendarView';
import isNil from 'lodash/isNil';
import {
  CalendarViewType,
  DATE_UNIT_BY_VIEW_TYPE,
  isCancelledOrNoShow,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  appointmentsEventChannel,
  insurancesEventChannel,
  patientAlertsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import EventCreateButton from 'pages/Dashboard/pages/Appointments/pages/List/components/EventCreateButton';
import {
  UPCOMING_ENCOUNTERS_REGEX,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientList';
import {
  useAppointmentNotificationEffect,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  AppointmentPatientAlertDTO,
} from 'dtos';
import {
  AlertSeverity,
} from 'dtos/alertSeverity';

type Props = {
  date: Dayjs;
  setDate: (value: Dayjs) => void;
  viewType: CalendarViewType;
  setViewType: (value: CalendarViewType) => void;
};

const viewOptions: CalendarViewType[] = [
  'Daily',
  'Weekly',
  'Monthly',
];

type AppointmentSummaryAppointment = {
  isTimeBlock?: boolean;
  isTimeAvailability?: boolean;
  patientAlerts?: AppointmentPatientAlertDTO[] | null;
};

type AlertSeverityCounts = {
  errorCount: number;
  warningCount: number;
};

function countAlertsBySeverity(
  appointments: AppointmentSummaryAppointment[],
): AlertSeverityCounts {
  return appointments.reduce<AlertSeverityCounts>((counts, appointment) => {
    if (appointment.isTimeBlock || appointment.isTimeAvailability) {
      return counts;
    }

    return (appointment.patientAlerts ?? []).reduce((alertCounts, alert) => {
      if (alert.severity === AlertSeverity.Error) {
        return {
          ...alertCounts,
          errorCount: alertCounts.errorCount + 1,
        };
      }

      if (alert.severity === AlertSeverity.Warning) {
        return {
          ...alertCounts,
          warningCount: alertCounts.warningCount + 1,
        };
      }

      return alertCounts;
    }, counts);
  }, {
    errorCount: 0,
    warningCount: 0,
  });
}

type AppointmentSummary = {
  appointmentsOnlyCount: number;
  incompleteCount: number;
  errorCount: number;
  warningCount: number;
  hasIncompleteCount: boolean;
};

function getAppointmentSummary({
  appointmentsOnlyCount,
  incompleteCount,
  errorCount,
  warningCount,
  hasIncompleteCount,
}: AppointmentSummary): AppointmentSummary {
  return {
    appointmentsOnlyCount,
    incompleteCount,
    errorCount,
    warningCount,
    hasIncompleteCount,
  };
}

const _AppointmentList = forwardRef(({
  date,
  setDate,
  viewType,
  setViewType,
}: Props, ref: ForwardedRef<HTMLElement>) => {
  const { settings: { appointmentSettings }, loadingUserSettings } = useUserSettings();
  const [settings, setSettings] = useState<CalendarViewSettings>(
    appointmentSettings ?? defaultCalendarViewSettings,
  );

  useEffect(() => {
    if (!loadingUserSettings && !isNil(appointmentSettings)) {
      setSettings(appointmentSettings);
    }
  }, [loadingUserSettings, appointmentSettings]);

  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  const [blockedRefresh, setBlockRefresh] = useState<boolean>(false);
  const {
    data: _data,
    filters,
    filteredData: _filteredData,
    setFilters,
    refetch,
  } = useContext(FilterContext);

  const data = useMemo(() => (
    settings?.includeCancelledAndNoShow
      ? _data
      : _data.filter((appointment) => !isCancelledOrNoShow(appointment))
  ), [_data, settings.includeCancelledAndNoShow]);

  const filteredData = useMemo(() => (
    settings?.includeCancelledAndNoShow
      ? _filteredData
      : _filteredData.filter((appointment) => !isCancelledOrNoShow(appointment))
  ), [_filteredData, settings.includeCancelledAndNoShow]);

  const incompletedAppointments = useMemo(() => (
    filteredData.filter((appointment) => (
      !UPCOMING_ENCOUNTERS_REGEX.test(appointment.patientAppointmentStatus)
      && !appointment.isTimeBlock && !appointment.isTimeAvailability
    ))
  ), [filteredData]);

  const appointmentsOnlyCount = useMemo(() => (
    filteredData.reduce((count, { isTimeBlock, isTimeAvailability, count: eventsCount }) => (
      !isTimeBlock && !isTimeAvailability ? count + (eventsCount ?? 1) : count), 0)
  ), [filteredData]);

  const {
    errorCount,
    warningCount,
  } = useMemo(
    () => countAlertsBySeverity(filteredData),
    [filteredData],
  );

  const handleViewChange = useCallback((incomingViewType: CalendarViewType) => {
    setDate(dayjs().startOf(DATE_UNIT_BY_VIEW_TYPE[incomingViewType]));
    setViewType(incomingViewType);
  }, [setViewType, setDate]);

  const formattedDate = formatDate(date);
  const {
    data: doctorList,
    isFetching: fetchingDoctors,
  } = useGetUsers();

  const normalizedDoctors = useMemo(() => doctorList?.map((doctor) => (
    {
      id: doctor.userId ?? 0,
      ...doctor,
    }
  )), [doctorList]);

  const doctors = useMemo(() => (
    filters.userId?.length > 0
      ? normalizedDoctors?.filter((doctor) => filters.userId.includes(doctor.userId)) ?? []
      : normalizedDoctors ?? []
  ), [normalizedDoctors, filters.userId]);

  const handleRefresh = (rerender: boolean = false) => {
    setForceRefresh(rerender);
    if (rerender) {
      refetch?.();
    }
  };

  useEffect(() => {
    const unsubscribeFromCreate = appointmentsEventChannel.on('onEventCreated', (created) => {
      if (created) {
        handleRefresh(true);
      }
    });

    const unsubscribeFromDelete = appointmentsEventChannel.on('onEventDeleted', () => {
      handleRefresh(true);
    });

    const unsubscribeFromUpdate = appointmentsEventChannel.on('onEventUpdated', (id, forceRerender) => {
      handleRefresh(Boolean(forceRerender));
    });

    const unsubscribeFromEligibility = insurancesEventChannel.on('onEligibilityCheckRefresh', () => {
      handleRefresh(true);
    });

    const unsubscribeFromPatientAlerts = patientAlertsEventChannel.on('onPatientAlertsChanged', () => {
      handleRefresh(true);
    });

    return () => {
      unsubscribeFromCreate();
      unsubscribeFromDelete();
      unsubscribeFromUpdate();
      unsubscribeFromEligibility();
      unsubscribeFromPatientAlerts();
    };
  }, []);

  const onEncounterUpdated = useCallback(() => {
    handleRefresh(!blockedRefresh);

    // this is temporary hack to prevent refreshing calendar view
    // when client it's a current client that made a change
    if (blockedRefresh) {
      setBlockRefresh(false);
    }
  }, [blockedRefresh]);

  useAppointmentNotificationEffect({
    onUpdate: onEncounterUpdated,
    date,
    viewType,
    dependencies: [blockedRefresh],
  });

  useEffect(() => {
    if (isNil(date)) {
      setDate(dayjs());
    }
  }, [date]);

  const hasIncompleteCount = viewType !== 'Monthly' && incompletedAppointments.length > 0;
  const appointmentSummary = getAppointmentSummary({
    appointmentsOnlyCount,
    incompleteCount: incompletedAppointments.length,
    errorCount,
    warningCount,
    hasIncompleteCount,
  });

  return (
    <div className="bg-primaryLight flex h-full max-h-[calc(100vh-1.5rem)] overflow-hidden">
      <div className="nw-appointments w-full flex-1 flex flex-col bg-white px-4 overflow-none pt-4 pb-24 auto-scrollable relative">
        <div className="flex justify-between items-start">
          <h1 className="font-semibold text-xl mb-4">Appointments</h1>
          {filteredData.length > 0 && (
            <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
              <span>
                {appointmentSummary.hasIncompleteCount ? (
                  <>
                    <span className="font-semibold text-gray-900">
                      {appointmentSummary.incompleteCount}
                      {' of '}
                      {appointmentSummary.appointmentsOnlyCount}
                    </span>
                    {' appointments left'}
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-gray-900">
                      {appointmentSummary.appointmentsOnlyCount}
                    </span>
                    {` ${pluralize('appointment', appointmentSummary.appointmentsOnlyCount, false)} in total`}
                  </>
                )}
              </span>
              {appointmentSummary.errorCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-red-500"
                  />
                  {appointmentSummary.errorCount} High
                </span>
              )}
              {appointmentSummary.warningCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-amber-500"
                  />
                  {appointmentSummary.warningCount} Medium
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex mb-4 relative z-[100]">
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-3">
              <Select<CalendarViewType>
                className="!border-gray-400 !rounded-md"
                id="visits-menu"
                placeholder="Daily"
                value={viewType}
                options={viewOptions}
                getLabel={(option: CalendarViewType) => option}
                getValue={(option: CalendarViewType) => option}
                onChange={(val: CalendarViewType | CalendarViewType[]) => {
                  handleViewChange(val as CalendarViewType);
                }}
                renderValue={(val: string) => (
                  <p>
                    View:
                    {' '}
                    <span className="font-semibold">{val}</span>
                  </p>
                )}
              />
              <Button
                variant="outlined"
                type="button"
                onClick={() => {
                  setDate(dayjs().startOf(DATE_UNIT_BY_VIEW_TYPE[viewType]));
                }}
              >
                Today
              </Button>
              <DatePicker
                className="nw-appointments-date-picker w-32 date-picker-input-semibold"
                showDateButtons
                value={date}
                onChange={(value) => {
                  if (!isNil(value)) {
                    setDate(value);
                  }
                }}
                accumulationUnit={DATE_UNIT_BY_VIEW_TYPE[viewType]}
              />
              <SettingsButton
                value={appointmentSettings ?? {}}
                filtersRef={ref as RefObject<HTMLDivElement>}
                onChange={setSettings}
                isLoading={loadingUserSettings}
              />
            </div>
            <div className="flex justify-right items-center gap-3">
              <EventCreateButton />
            </div>
          </div>
        </div>
        {fetchingDoctors ? (
          <div className="flex flex-1 h-full items-center justify-center">
            <Spinner className="!font-bold" size={30} />
            <span className="mx-2">Loading...</span>
          </div>
        ) : (
          <CalendarView
            settings={settings}
            appointments={data}
            filteredAppointments={filteredData}
            setFilters={setFilters}
            forceRefresh={forceRefresh}
            doctors={doctors}
            setDate={setDate}
            setBlockRefresh={setBlockRefresh}
            currentDate={formattedDate}
            viewType={viewType}
            setViewType={setViewType}
          />
        )}
      </div>
    </div>
  );
});

export default memo(_AppointmentList);
